import { create } from "zustand";

import {
  fetchAccountAnalysisConfiguration,
  invokeSyncPipelineConfig
} from "../../data/queries/accountAnalysis";
import { PIPELINE_CONFIG_SYNC_DEBOUNCE_MS } from "../constants";
import { PipelineConfigSyncStatus } from "../types/pipelineConfigSync";

export interface PipelineConfigSyncState {
  isSyncing: boolean;
  syncError: string | null;
  pipelineConfigSyncEnabled: boolean;
  lastSyncAt: Date | null;
  lastSyncStatus: PipelineConfigSyncStatus | null;
}

interface PipelineConfigSyncActions {
  triggerPipelineConfigSync: (tenantId: string) => void;
  setPipelineConfigSyncEnabled: (enabled: boolean) => void;
  refreshSyncStatus: () => Promise<void>;
}

export type PipelineConfigSyncStore = PipelineConfigSyncState & PipelineConfigSyncActions;

type PipelineConfigSyncableStore = { configuration?: { tenant_id?: string } | null };

type AsyncAction<TArgs extends unknown[]> = (...args: TArgs) => Promise<void>;

export const withPipelineConfigSync =
  (get: () => PipelineConfigSyncableStore): (<TArgs extends unknown[]>(fn: AsyncAction<TArgs>) => AsyncAction<TArgs>) =>
    <TArgs extends unknown[]>(fn: AsyncAction<TArgs>): AsyncAction<TArgs> =>
      async (...args) => {
        await fn(...args);
        const tenantId = get().configuration?.tenant_id;
        if (tenantId) {
          usePipelineConfigSyncStore.getState().triggerPipelineConfigSync(tenantId);
        }
      };

const syncDebounceTimer: { current: ReturnType<typeof setTimeout> | null } = { current: null };

export const usePipelineConfigSyncStore = create<PipelineConfigSyncStore>()((set, get) => ({
  isSyncing: false,
  syncError: null,
  pipelineConfigSyncEnabled: false,
  lastSyncAt: null,
  lastSyncStatus: null,

  setPipelineConfigSyncEnabled: (enabled) => set({ pipelineConfigSyncEnabled: enabled }),

  refreshSyncStatus: async () => {
    const config = await fetchAccountAnalysisConfiguration();
    if (!config) {
      return;
    }

    set({
      lastSyncAt: config.pipeline_sync_last_success
        ? new Date(config.pipeline_sync_last_success)
        : null,
      lastSyncStatus: (config.pipeline_sync_status?.status as PipelineConfigSyncStatus) ?? null,
      syncError: config.pipeline_sync_status?.error_message ?? null
    });
  },

  triggerPipelineConfigSync: (tenantId: string) => {
    if (syncDebounceTimer.current) {
      clearTimeout(syncDebounceTimer.current);
      syncDebounceTimer.current = null;
    }

    if (!get().pipelineConfigSyncEnabled) {
      set({ isSyncing: false });
      return;
    }

    set({ isSyncing: true });

    syncDebounceTimer.current = setTimeout(async () => {
      try {
        await invokeSyncPipelineConfig(tenantId);
      } catch {
        // API failure is captured by server-side pipeline_sync_status
      } finally {
        set({ isSyncing: false });
        try {
          await get().refreshSyncStatus();
        } catch {
          // refreshSyncStatus failure is non-critical; status will update on next poll
        }
      }
    }, PIPELINE_CONFIG_SYNC_DEBOUNCE_MS);
  }
}));
